import { FC, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from 'hooks';
import {
  isMember,
  isOtherEmployee,
  isPhysicalAccount,
  isWorkerNew,
} from 'utils';
import { IMember, IPhysicalAccount, IServiceWorker, IWorkerNew } from 'types';
import { HStack, Rating } from 'components';
import { Text } from 'components/ui';
import { Communications } from './components';
import {
  BottomBlock,
  PositionDivider,
  PositionSpan,
  PositionWrapper,
  Status,
  UpperBlock,
  Wrapper,
} from './applicant-card.styled';

interface IProps {
  applicant: IMember | IWorkerNew | IServiceWorker | IPhysicalAccount;
  selected: boolean;
  collapsed: boolean;
  service_name?: string;
  organizationId?: string;
  physicalId?: string;
  canCollapsed?: boolean;
  withoutCallback?: boolean;
  id?: string;
  inCallCard?: boolean;
  card?: any;
}

export const ApplicantCard: FC<IProps> = observer(
  ({
    applicant,
    selected,
    collapsed,
    service_name = '',
    organizationId = '',
    physicalId = '',
    canCollapsed = true,
    withoutCallback = false,
    id,
    inCallCard,
    card,
  }) => {
    const { createRequest, telephony, aiRequests } = useStore();
    const { setSelectedId } = createRequest.applicant;

    const [hovered, setHovered] = useState<boolean>(false);

    const memberStatus: string = useMemo(() => {
      if (isMember(applicant)) {
        if (applicant._type.includes('OtherTenant')) {
          return 'Иное лицо';
        } else if (applicant.is_responsible) {
          return 'Ответственный';
        }
      }

      return '';
    }, [applicant]);

    const changeSelectedApplicant = (): void => {
      if (isMember(applicant)) {
        createRequest.setBooleanProperties('anotherMember', false);
        setSelectedId('memberId', applicant.id);
      } else if (isWorkerNew(applicant)) {
        setSelectedId('workerId', applicant.id);
      } else if (isOtherEmployee(applicant)) {
        setSelectedId('organizationId', organizationId);
        setSelectedId('legalId', applicant.id);
      } else if (isPhysicalAccount(applicant)) {
        setSelectedId('physicalId', physicalId);
      }

      updateCallerInfo();
    };

    function isMemberType(item: any): item is IMember {
      return 'can_vote' in item;
    }

    const updateCallerInfo = (): void => {
      if (inCallCard) {
        const typeMember = isMemberType(applicant);
        const callerInfo = card ? card.callerInfo : telephony.callerInfo;

        !typeMember && callerInfo.setCallerAddress('');
        !typeMember && callerInfo.setCallerNumber('');

        callerInfo.setCallerName(applicant.str_name);
        callerInfo.setCallerEmail(applicant.email || '');
        callerInfo.setCallerPhone(
          applicant.phones[0]?.str_number || createRequest.initialPhoneNumber
        );
      } else {
        const phone =
          (applicant.phones[0]?.str_number ??
            applicant.phones[0]?.code + applicant.phones[0]?.number) ||
          '';

        if (!phone) {
          return;
        }

        createRequest.setPhoneNumber('initialPhoneNumber', phone);
        createRequest.setPhoneNumber('lastValidPhoneNumber', phone);
      }
    };

    return (
      <Wrapper
        hovered={hovered}
        selected={selected}
        collapsed={collapsed}
        canCollapsed={canCollapsed}
        isDisabled={aiRequests.isAiMode}
      >
        <UpperBlock
          collapsed={collapsed}
          onMouseOver={() => setHovered(true)}
          onMouseOut={() => setHovered(false)}
          onClick={changeSelectedApplicant}
        >
          <HStack align="center" spacing="xs">
            <Text>{applicant.str_name}</Text>
            {!!memberStatus && (
              <Status isResponsibility={memberStatus === 'Ответственный'}>
                {memberStatus}
              </Status>
            )}

            {isMember(applicant) && (
              <div style={{ marginLeft: 'auto' }}>
                <Rating tenantId={applicant.id} />
              </div>
            )}
          </HStack>

          {(isWorkerNew(applicant) || isOtherEmployee(applicant)) && (
            <PositionWrapper>
              <PositionSpan>
                {isWorkerNew(applicant)
                  ? applicant.department.name
                  : service_name}
              </PositionSpan>
              <PositionDivider />
              <PositionSpan>
                {isWorkerNew(applicant)
                  ? applicant.position.name
                  : applicant.position}
              </PositionSpan>
            </PositionWrapper>
          )}
        </UpperBlock>

        <BottomBlock>
          <Communications
            id={isPhysicalAccount(applicant) ? physicalId : applicant.id}
            phones={applicant.phones}
            mail={applicant.email}
            withoutCallback={withoutCallback}
          />
        </BottomBlock>
      </Wrapper>
    );
  }
);
